import { createContext, useContext, ReactNode } from "react";

/**
 * Static/no-auth build (knaxly.dev/quanta on GitHub Pages).
 *
 * The original app used Azure MSAL sign-in plus a backend API for cloud sync,
 * sharing and the marketplace. This hosted build is fully client-side and has
 * no backend, so auth is disabled: `isAuthenticated` is always false and the
 * cloud entry points are hidden in the UI. This stub keeps the same
 * `useAuth()` interface so the rest of the app compiles and runs unchanged
 * (session sync and other cloud hooks already no-op when not authenticated).
 */

interface AuthUser {
  name?: string;
  username?: string;
  localAccountId?: string;
}

interface AuthContextValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: AuthUser | null;
  userOid: string | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
}

const NOOP_AUTH: AuthContextValue = {
  isAuthenticated: false,
  isLoading: false,
  user: null,
  userOid: null,
  login: async () => {},
  logout: async () => {},
  getAccessToken: async () => null,
};

const AuthContext = createContext<AuthContextValue>(NOOP_AUTH);

export function AuthProvider({ children }: { children: ReactNode }) {
  return <AuthContext.Provider value={NOOP_AUTH}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  return useContext(AuthContext);
}
